import { useAuth } from '../../utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { priceProfiles as profilesApi } from '../../utils/api';
import { Plus, Edit2, Trash2, Save, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const HC_COLOR = '#00d2b4';
const HP_COLOR = '#f5a623';

// ── Éditeur de tranches ───────────────────────────────────────
function TiersEditor({ profileId, onSaved }) {
  const [tiers, setTiers]   = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    profilesApi.getTiers(profileId).then(r => {
      const t = r.data.tiers;
      setTiers(t.length > 0 ? t.map(tier => ({
        minPlayers:      tier.min_players,
        maxPlayers:      tier.max_players || '',
        mode:            tier.price_flat ? 'flat' : 'per_person',
        // Standard
        // HC
        pricePerPersonHc: tier.price_per_person_hc || '',
        priceFlatHc:      tier.price_flat_hc        || '',
        // HP
        pricePerPersonHp: tier.price_per_person_hp || '',
        priceFlatHp:      tier.price_flat_hp        || '',
      })) : [defaultTier()]);
    });
  }, [profileId]);

  function defaultTier() {
    return { minPlayers: 1, maxPlayers: '', mode: 'per_person',
      pricePerPersonHc: '', priceFlatHc: '',
      pricePerPersonHp: '', priceFlatHp: '' };
  }

  const addTier = () => setTiers(p => [...p, defaultTier()]);
  const delTier = idx => setTiers(p => p.filter((_, i) => i !== idx));
  const upd     = (idx, k, v) => setTiers(p => p.map((t, i) => i === idx ? { ...t, [k]: v } : t));

  const save = async () => {
    setSaving(true);
    try {
      await profilesApi.setTiers(profileId, { tiers });
      toast.success('Tranches sauvegardées');
      onSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  // Rendu d'un input prix selon le mode (per_person ou flat)
  const priceInput = (idx, t, stdKey, flatKey, placeholder, accentColor) => (
    <input type="number" step="0.01" min={0} className="input"
      placeholder={placeholder}
      value={t.mode === 'flat' ? t[flatKey] : t[stdKey]}
      style={{ borderColor: t.mode === 'flat' ? (t[flatKey] ? accentColor : undefined) : (t[stdKey] ? accentColor : undefined), transition: 'border-color 0.15s' }}
      onChange={e => upd(idx, t.mode === 'flat' ? flatKey : stdKey, e.target.value)}/>
  );

  return (
    <div style={{ padding: '14px 0 0' }}>
      {/* En-tête colonnes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '70px 70px 90px 1fr 1fr auto',
        gap: 8, alignItems: 'center',
        padding: '0 12px 8px',
        fontSize: 11, fontWeight: 700, color: 'var(--text-3)', letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}>
        <div>De (j.)</div>
        <div>À (opt.)</div>
        <div>Mode</div>
        <div style={{ color: HC_COLOR }}>HC</div>
        <div style={{ color: HP_COLOR }}>HP</div>
        <div/>
      </div>

      {tiers.map((t, idx) => (
        <div key={idx} style={{
          display: 'grid',
          gridTemplateColumns: '70px 70px 90px 1fr 1fr auto',
          gap: 8, alignItems: 'center', marginBottom: 8,
          padding: '10px 12px', background: 'var(--bg-base)', borderRadius: 8,
        }}>
          {/* De */}
          <input type="number" min={1} className="input" value={t.minPlayers}
            onChange={e => upd(idx, 'minPlayers', e.target.value)}/>

          {/* À */}
          <input type="number" min={1} className="input" value={t.maxPlayers} placeholder="∞"
            onChange={e => upd(idx, 'maxPlayers', e.target.value)}/>

          {/* Mode */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 5, overflow: 'hidden' }}>
              {[['per_person', '/pers'], ['flat', 'Fixe']].map(([m, l]) => (
                <button key={m} type="button" onClick={() => upd(idx, 'mode', m)}
                  style={{
                    flex: 1, padding: '4px 0', border: 'none', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 10,
                    background: t.mode === m ? 'var(--accent)' : 'var(--bg-elevated)',
                    color: t.mode === m ? '#fff' : 'var(--text-2)',
                  }}>{l}</button>
              ))}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-3)', textAlign: 'center' }}>
              {t.mode === 'flat' ? '€ fixe' : '€/pers'}
            </div>
          </div>

          {/* HC */}
          <div>
            {priceInput(idx, t, 'pricePerPersonHc', 'priceFlatHc',
              t.mode === 'flat' ? '90.00' : '18.00', HC_COLOR)}
          </div>

          {/* HP */}
          <div>
            {priceInput(idx, t, 'pricePerPersonHp', 'priceFlatHp',
              t.mode === 'flat' ? '140.00' : '30.00', HP_COLOR)}
          </div>

          {/* Supprimer */}
          {tiers.length > 1 ? (
            <button type="button" className="btn btn-ghost btn-sm" style={{ padding: 4 }}
              onClick={() => delTier(idx)}>
              <Trash2 size={12} color="var(--red)"/>
            </button>
          ) : <div/>}
        </div>
      ))}

      {/* Légende */}
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-3)', padding: '4px 12px', marginBottom: 8 }}>
        <span>Standard = tarif par défaut (aucune période active)</span>
        <span style={{ color: HC_COLOR }}>HC = heures creuses</span>
        <span style={{ color: HP_COLOR }}>HP = heures pleines</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={addTier}>
          <Plus size={12}/> Ajouter une tranche
        </button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving} style={{ marginLeft: 'auto' }}>
          <Save size={12}/> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </button>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
// `embedded` : rendu comme onglet interne (ex: page Tarification) — on
// masque alors le gros titre/sous-titre pour ne pas le dupliquer avec celui
// de la page hôte, mais on garde le bouton d'action.
export default function AdminPriceProfiles({ embedded = false }) {
  const { can } = useAuth();
  if (!can('admin.pricing')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [profiles, setProfiles] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing]   = useState(null);
  const [editName, setEditName] = useState('');
  const [newName, setNewName]   = useState('');
  const [creating, setCreating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    profilesApi.list().then(r => setProfiles(r.data.profiles));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    try {
      await profilesApi.create({ name: newName });
      toast.success('Profil créé');
      setNewName(''); setCreating(false); load();
    } catch { toast.error('Erreur'); }
  };

  const rename = async (id) => {
    if (!editName.trim()) return;
    try {
      await profilesApi.update(id, { name: editName });
      toast.success('Profil renommé');
      setEditing(null); load();
    } catch { toast.error('Erreur'); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await profilesApi.remove(confirmRemove.id);
      toast.success('Profil désactivé');
      setConfirmRemove(null);
      load();
    } catch { toast.error('Erreur'); }
    finally { setRemoving(false); }
  };

  const newProfileBtn = (
    <button className="btn btn-primary" onClick={() => setCreating(true)}>
      <Plus size={15}/> Nouveau profil
    </button>
  );

  return (
    <>
      {embedded ? (
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>{newProfileBtn}</div>
      ) : (
        <div className="page-header">
          <div>
            <h2>Profils tarifaires</h2>
            <p>Définissez vos profils et leurs tarifs Standard / HC / HP par tranche de joueurs</p>
          </div>
          {newProfileBtn}
        </div>
      )}

      <div className={embedded ? undefined : 'page-body'}>
        {creating && (
          <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
            <input className="input" style={{ flex: 1 }} autoFocus
              placeholder="Nom du profil (ex: Normal, Étudiant, Groupe…)"
              value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); create(); } }}/>
            <button className="btn btn-primary" onClick={create}>Créer</button>
            <button className="btn btn-secondary" onClick={() => { setCreating(false); setNewName(''); }}>Annuler</button>
          </div>
        )}

        {profiles.length === 0 && !creating && (
          <div className="empty-state card">
            <p>Aucun profil tarifaire — créez-en un pour commencer</p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {profiles.map(p => (
            <div key={p.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px' }}>
                {editing === p.id ? (
                  <>
                    <input className="input" style={{ flex: 1 }} autoFocus value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); rename(p.id); } }}/>
                    <button className="btn btn-primary btn-sm" onClick={() => rename(p.id)}>OK</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>✕</button>
                  </>
                ) : (
                  <>
                    <h3 style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>{p.name}</h3>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(p.id); setEditName(p.name); }}>
                      <Edit2 size={13}/>
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRemove(p)}>
                      <Trash2 size={13} color="var(--red)"/>
                    </button>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                      Tranches {expanded === p.id ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                    </button>
                  </>
                )}
              </div>

              {expanded === p.id && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '0 20px 16px' }}>
                  <p className="text-sm text-muted" style={{ margin: '12px 0 4px' }}>
                    Tranches <strong>par défaut</strong> pour ce profil — surchargeables salle par salle dans "Salles &amp; Tarifs".
                  </p>
                  <TiersEditor profileId={p.id} onSaved={load}/>
                </div>
              )}
            </div>
          ))}
        </div>

        {profiles.length > 0 && (
          <div className="card" style={{ marginTop: 20, background: 'rgba(124,92,252,0.05)', borderColor: 'rgba(124,92,252,0.2)' }}>
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
              <strong>Comment ça marche :</strong> le prix appliqué dépend de la période active au moment du créneau.
              Si aucune période HC ou HP ne correspond, le tarif <strong>Standard</strong> s'applique.
              Les périodes se configurent dans <strong>Périodes tarifaires</strong>.
            </div>
          </div>
        )}
      </div>
      {confirmRemove && (
        <ConfirmModal
          title="Désactiver le profil"
          message={`Désactiver "${confirmRemove.name}" ? Les tarifs existants ne seront pas supprimés.`}
          confirmLabel="Désactiver"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </>
  );
}
